import React, { useState, useContext } from "react";
import PropTypes from "prop-types";

import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";

import "emoji-mart/css/emoji-mart.css";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import api from "../../services/api";
import { Menu } from "@material-ui/core";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import ForwardMessageModal from "../ForwardMessageModal";

// 2026-06-08 (Amit/Raviv): removed "delete message", "edit message" and
// "message history" menu items — these features depend on Baileys APIs
// (raw protocol revoke + edit) that the WhatsApp Business / Cloud API
// does not expose. Keeping them in the menu produced 500s for Cloud-API
// tenants when an agent tried to use them. Reply / Forward / Reaction /
// Download are channel-agnostic and stay.

// Default reactions — used as the baseline when localStorage is empty
// (first-time user). Each agent can then customise their own list via
// the + / × buttons inside the reactions menu (Raviv 2026-06-08).
// Lazy — emoji-mart (Picker + emoji data, heavy) loads only when the
// picker first opens, not in the entry bundle.
const Picker = React.lazy(() =>
  import("emoji-mart").then((m) => ({ default: m.Picker }))
);

const DEFAULT_REACTIONS = ["👍", "❤️", "😂", "😮", "🙌", "🙏🏽", "👏", "🎉"];
const REACTIONS_LS_KEY = "customReactions";

const MessageOptionsMenu = ({
	message,
	data,
	menuOpen,
	handleClose,
	anchorEl,
	setShowSelectCheckbox,
	showSelectCheckBox,
  	forwardMessageModalOpen,
  	setForwardMessageModalOpen,
  	selectedMessages,
	setReaction, // New prop to pass reaction data to parent component
	mediaUrl,    // URL of attached media — enables Download option
	// Sprint 8A (Gal 2026-06-08): when the hover-row's 😊 button is
	// clicked in MessagesList, the parent sets quickReactionAnchor to
	// the button's element. We then skip the full options menu and
	// open just the reactions sub-menu anchored to that button. The
	// parent receives close events via onCloseQuickReaction.
	quickReactionAnchor,
	onCloseQuickReaction,
 }) => {
	const { setReplyingMessage } = useContext(ReplyMessageContext);

	const [reactionsMenuOpen, setReactionsMenuOpen] = useState(false); // State for reactions menu
	const [reactionsAnchorEl, setReactionsAnchorEl] = useState(null); // Anchor for reactions menu
	// Custom reactions (Raviv 2026-06-08): list lives in localStorage so it
	// persists per browser profile. First-time users see DEFAULT_REACTIONS.
	const [reactions, setReactions] = useState(() => {
		try {
			const stored = JSON.parse(localStorage.getItem(REACTIONS_LS_KEY) || "null");
			return Array.isArray(stored) && stored.length > 0 ? stored : DEFAULT_REACTIONS;
		} catch {
			return DEFAULT_REACTIONS;
		}
	});
	const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

	const persistReactions = (next) => {
		setReactions(next);
		try {
			localStorage.setItem(REACTIONS_LS_KEY, JSON.stringify(next));
		} catch { /* localStorage may be disabled — fall back to in-memory only */ }
	};

	const handleAddCustomReaction = (emoji) => {
		const ch = emoji?.native || "";
		if (!ch || reactions.includes(ch)) {
			setEmojiPickerOpen(false);
			return;
		}
		persistReactions([...reactions, ch]);
		setEmojiPickerOpen(false);
	};

	const handleRemoveCustomReaction = (e, emoji) => {
		e.stopPropagation(); // don't trigger the parent reaction select
		persistReactions(reactions.filter((r) => r !== emoji));
	};

	const handleSetShowSelectCheckbox = () => {
		setShowSelectCheckbox(!showSelectCheckBox);
		handleClose();
	};

	const handleReplyMessage = () => {
		setReplyingMessage(message);
		handleClose();
	};

	// Sprint 8B (Gal 2026-06-08): copy message body to clipboard.
	// Falls back to a hidden textarea + execCommand("copy") for browsers
	// that withhold navigator.clipboard (insecure context, old WebView,
	// some Capacitor builds). Always closes the menu and shows a toast,
	// even on failure — silent clipboard ops feel broken.
	const handleCopyMessage = async () => {
		const text = message?.body || "";
		handleClose();
		if (!text) return;
		try {
			if (navigator.clipboard && window.isSecureContext) {
				await navigator.clipboard.writeText(text);
			} else {
				const ta = document.createElement("textarea");
				ta.value = text;
				ta.style.position = "fixed";
				ta.style.opacity = "0";
				document.body.appendChild(ta);
				ta.focus();
				ta.select();
				document.execCommand("copy");
				document.body.removeChild(ta);
			}
			toast.success(i18n.t("messageOptionsMenu.copied"));
		} catch (err) {
			toastError(err);
		}
	};

	// Body-only messages and media-with-caption get the Copy option;
	// pure media / placeholders (Voice/Image/Video/Document) and auto-
	// generated filenames don't — there's nothing meaningful to copy.
	const canCopyBody = (() => {
		const body = message?.body;
		if (!body || typeof body !== "string") return false;
		if (/^\[(Image|Video|Document|Voice message|Audio|Sticker).*\]$/.test(body)) return false;
		if (/^[\w.\-]+\.[a-z0-9]{1,8}$/i.test(body)) return false;
		if (body.startsWith("data:image")) return false;
		return true;
	})();

	const handleOpenReactionsMenu = (e) => {
		setReactionsAnchorEl(e.currentTarget);
		setReactionsMenuOpen(true);
		handleClose();
	  };

	  const handleCloseReactionsMenu = () => {
		setReactionsAnchorEl(null);
		setReactionsMenuOpen(false);
	  };

	  // Sprint 8A — the reactions sub-menu can be opened in two ways:
	  //   1. Full options menu → "Reactions" item → internal state
	  //   2. Hover-row 😊 button → quickReactionAnchor prop (external)
	  // Derive a single effective anchor + open flag so the render and
	  // close logic don't have to branch in two places.
	  const effectiveReactionsAnchor = quickReactionAnchor || reactionsAnchorEl;
	  const effectiveReactionsOpen = Boolean(quickReactionAnchor) || reactionsMenuOpen;
	  const closeReactionsMenu = () => {
		if (quickReactionAnchor && typeof onCloseQuickReaction === "function") {
		  onCloseQuickReaction();
		} else {
		  handleCloseReactionsMenu();
		}
		// Always reset the local picker; the picker state is purely UI
		// and shouldn't leak across two opens of the menu.
		setEmojiPickerOpen(false);
	  };

	  const handleSelectReaction = async (reaction) => {
		closeReactionsMenu();
		try {
		  await api.post(`/messages/${message.id}/reactions`, {
			reaction
		  });

		  setReaction(message.id, reaction);
		} catch (err) {
		  toastError(err);
		}
	  };

	return (
		<>
				<ForwardMessageModal
				modalOpen={forwardMessageModalOpen}
				message={message}
				onClose={(e) => {
					setForwardMessageModalOpen(false);
					setShowSelectCheckbox(false);
				}}
				messages={selectedMessages}
			/>
			<Menu
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				open={menuOpen}
				onClose={handleClose}
				PaperProps={{
					style: {
						direction: 'rtl',
						textAlign: 'right',
					}
				}}
			>
				<MenuItem onClick={handleReplyMessage}>
					{i18n.t("messageOptionsMenu.reply")}
				</MenuItem>
				{canCopyBody && (
					<MenuItem onClick={handleCopyMessage}>
						{i18n.t("messageOptionsMenu.copy")}
					</MenuItem>
				)}
				<MenuItem onClick={handleSetShowSelectCheckbox}>
					{i18n.t("messageOptionsMenu.forward")}
				</MenuItem>
				<MenuItem onClick={handleOpenReactionsMenu}>
					{i18n.t("messageOptionsMenu.reactions")}
				</MenuItem>
				{mediaUrl && (
					<MenuItem
						component="a"
						href={mediaUrl}
						download
						onClick={handleClose}
					>
						{i18n.t("messageOptionsMenu.download")}
					</MenuItem>
				)}
			</Menu>

			<Menu
				anchorEl={effectiveReactionsAnchor}
				getContentAnchorEl={null}
				anchorOrigin={{
				vertical: "bottom",
				horizontal: "right",
				}}
				transformOrigin={{
				vertical: "top",
				horizontal: "right",
				}}
				open={effectiveReactionsOpen}
				onClose={closeReactionsMenu}
			>
				  <div style={{
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
 }}>
				  {reactions.map((reaction) => (
				<MenuItem
					style={{
						cursor: "pointer",
						borderRadius: 5,
						'&:hover': {
						  backgroundColor: '#888a'
						},
						flexBasis: '25%',
						margin: 0,
						textAlign: 'center',
						position: 'relative',
					  }}
					key={reaction}
					onClick={() => handleSelectReaction(reaction === message.reaction ? "" : reaction)}
				>
					{reaction}
					{/* × button — appears on hover via the parent flex item.
						stopPropagation prevents accidental reaction-send. */}
					<IconButton
						size="small"
						aria-label="remove reaction"
						onClick={(e) => handleRemoveCustomReaction(e, reaction)}
						style={{
							position: 'absolute',
							top: -4,
							right: -4,
							padding: 1,
							background: 'rgba(0,0,0,0.55)',
							color: '#fff',
						}}
					>
						<CloseIcon style={{ fontSize: 10 }} />
					</IconButton>
				</MenuItem>
				))}
				{/* + button — opens emoji-mart Picker to append a new emoji */}
				<MenuItem
					style={{
						cursor: "pointer",
						borderRadius: 5,
						flexBasis: '25%',
						margin: 0,
						textAlign: 'center',
					}}
					key="add-reaction"
					onClick={(e) => {
						e.stopPropagation();
						setEmojiPickerOpen((v) => !v);
					}}
				>
					<AddIcon style={{ fontSize: 18 }} />
				</MenuItem>
          </div>
				{emojiPickerOpen && (
					<div style={{ padding: 4 }}>
						<React.Suspense fallback={null}>
<Picker
							native
							perLine={8}
							emojiSize={20}
							showPreview={false}
							showSkinTones={false}
							onSelect={handleAddCustomReaction}
						/>
</React.Suspense>
					</div>
				)}
				{message.reaction && (
				<MenuItem
					key="remove-reaction"
					onClick={() => handleSelectReaction("")}
				>
					{i18n.t("messageOptionsMenu.removeReactions")}
					</MenuItem>
				)}
			</Menu>
		</>
	);
};

MessageOptionsMenu.propTypes = {
  message: PropTypes.object,
  menuOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  anchorEl: PropTypes.object
}

export default MessageOptionsMenu;
